/**
 * Generated by orval 🍺
 * Do not edit manually.
 * Nucleus Admin API
 * Headless, multi-tenant commerce backend for jewelry brands.
 *
 * ## Surfaces
 *
 * | Prefix | Caller | Credential |
 * |---|---|---|
 * | `/platform/v1` | Platform operator | Platform token from **Platform: Auth** |
 * | `/admin/v1` | Staff of one organization | Staff token from **Admin: Auth** |
 * | `/storefront/v1` | A storefront | Store API key from **Admin: API keys** |
 * | `/public/v1` | Anyone (the brand application form) | None; rate limited per IP |
 *
 * Every credential is sent as `Authorization: Bearer <value>`. Use the **Authorize** button once and it applies to every request you try here.
 *
 * ## Conventions
 *
 * - **Ids** are UUID v7 strings. **Timestamps** are ISO 8601 in UTC. **Money** is `{ amount, currency }` with `amount` in integer minor units.
 * - **Lists** paginate by cursor: `?limit=20&cursor=...` in, `{ items, nextCursor }` out, `nextCursor` is `null` on the last page.
 * - **Errors** always use one envelope, `{ error: { code, message, requestId, details?, meta? } }`. Switch on `code`, never on `message`.
 * - **Request ids**: every response carries `x-request-id`. Quote it when reporting a problem.
 * - **Idempotency**: endpoints that create money-related state require an `Idempotency-Key` header; the header is listed on those endpoints.
 * - A record that belongs to another tenant answers **404**, never 403, so existence is not leaked.
 * OpenAPI spec version: 0.1.0
 */
import type {
  CompleteDraftOrderRequest,
  DeleteAdminV1StoresStoreIdDraftOrdersIdPathParameters,
  DraftInvoicePreview,
  DraftOrder,
  GetAdminV1StoresStoreIdDraftOrders200,
  GetAdminV1StoresStoreIdDraftOrdersIdInvoicePreviewParams,
  GetAdminV1StoresStoreIdDraftOrdersIdInvoicePreviewPathParameters,
  GetAdminV1StoresStoreIdDraftOrdersIdPathParameters,
  GetAdminV1StoresStoreIdDraftOrdersParams,
  GetAdminV1StoresStoreIdDraftOrdersPathParameters,
  GetAdminV1StoresStoreIdDraftOrdersPaymentOptions200,
  GetAdminV1StoresStoreIdDraftOrdersPaymentOptionsPathParameters,
  PatchAdminV1StoresStoreIdDraftOrdersIdBody,
  PatchAdminV1StoresStoreIdDraftOrdersIdLinesLineIdBody,
  PatchAdminV1StoresStoreIdDraftOrdersIdLinesLineIdPathParameters,
  PatchAdminV1StoresStoreIdDraftOrdersIdPathParameters,
  PostAdminV1StoresStoreIdDraftOrdersBody,
  PostAdminV1StoresStoreIdDraftOrdersIdCancelPathParameters,
  PostAdminV1StoresStoreIdDraftOrdersIdComplete201,
  PostAdminV1StoresStoreIdDraftOrdersIdCompletePathParameters,
  PostAdminV1StoresStoreIdDraftOrdersIdDuplicatePathParameters,
  PostAdminV1StoresStoreIdDraftOrdersIdSendInvoiceBody,
  PostAdminV1StoresStoreIdDraftOrdersIdSendInvoicePathParameters,
  PostAdminV1StoresStoreIdDraftOrdersPathParameters
} from '../model';

import { customInstance } from '../../../transport/mutator';
import type { BodyType } from '../../../transport/mutator';


type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];


  /**
 * total_gte, total_lte and sort=total use the total stored at each draft's last write
 * @summary Draft orders of the store, newest first
 */
export const getAdminV1StoresStoreIdDraftOrders = (
    { storeId }: GetAdminV1StoresStoreIdDraftOrdersPathParameters,
    params?: GetAdminV1StoresStoreIdDraftOrdersParams,
 options?: SecondParameter<typeof customInstance<GetAdminV1StoresStoreIdDraftOrders200>>,) => {
      return customInstance<GetAdminV1StoresStoreIdDraftOrders200>(
      {url: `/admin/v1/stores/${storeId}/draft-orders`, method: 'GET',
        params
    },
      options);
    }
  /**
 * Variant lines are priced from the store's prices in the store currency unless a unit price is given; custom lines are typed in. A file customization takes `{ key, value: { mediaId } }` from the organization's media library. The draft is re-priced on every read and lists what still blocks invoicing or completion
 * @summary Start a draft order
 */
export const postAdminV1StoresStoreIdDraftOrders = (
    { storeId }: PostAdminV1StoresStoreIdDraftOrdersPathParameters,
    postAdminV1StoresStoreIdDraftOrdersBody: BodyType<PostAdminV1StoresStoreIdDraftOrdersBody>,
 options?: SecondParameter<typeof customInstance<DraftOrder>>,) => {
      return customInstance<DraftOrder>(
      {url: `/admin/v1/stores/${storeId}/draft-orders`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: postAdminV1StoresStoreIdDraftOrdersBody
    },
      options);
    }
  /**
 * The store's active manual methods (key, name, instructions, fee) for the complete dialog; readable with orders.write, unlike the payments settings
 * @summary Manual payment methods a draft can be completed with
 */
export const getAdminV1StoresStoreIdDraftOrdersPaymentOptions = (
    { storeId }: GetAdminV1StoresStoreIdDraftOrdersPaymentOptionsPathParameters,
 options?: SecondParameter<typeof customInstance<GetAdminV1StoresStoreIdDraftOrdersPaymentOptions200>>,) => {
      return customInstance<GetAdminV1StoresStoreIdDraftOrdersPaymentOptions200>(
      {url: `/admin/v1/stores/${storeId}/draft-orders/payment-options`, method: 'GET'
    },
      options);
    }
  /**
 * @summary One draft order, re-priced
 */
export const getAdminV1StoresStoreIdDraftOrdersId = (
    { storeId, id }: GetAdminV1StoresStoreIdDraftOrdersIdPathParameters,
 options?: SecondParameter<typeof customInstance<DraftOrder>>,) => {
      return customInstance<DraftOrder>(
      {url: `/admin/v1/stores/${storeId}/draft-orders/${id}`, method: 'GET'
    },
      options);
    }
  /**
 * Lines replace the whole list; send a file customization back as its upload id to keep it, or `{ mediaId }` to replace it. 409 once the draft is completed or canceled
 * @summary Change lines, customer, addresses, shipping, discount or notes
 */
export const patchAdminV1StoresStoreIdDraftOrdersId = (
    { storeId, id }: PatchAdminV1StoresStoreIdDraftOrdersIdPathParameters,
    patchAdminV1StoresStoreIdDraftOrdersIdBody: BodyType<PatchAdminV1StoresStoreIdDraftOrdersIdBody>,
 options?: SecondParameter<typeof customInstance<DraftOrder>>,) => {
      return customInstance<DraftOrder>(
      {url: `/admin/v1/stores/${storeId}/draft-orders/${id}`, method: 'PATCH',
      headers: {'Content-Type': 'application/json', },
      data: patchAdminV1StoresStoreIdDraftOrdersIdBody
    },
      options);
    }
  /**
 * 409 for an invoiced, completed or canceled draft; cancel those instead
 * @summary Delete a draft that was never invoiced
 */
export const deleteAdminV1StoresStoreIdDraftOrdersId = (
    { storeId, id }: DeleteAdminV1StoresStoreIdDraftOrdersIdPathParameters,
 options?: SecondParameter<typeof customInstance<void>>,) => {
      return customInstance<void>(
      {url: `/admin/v1/stores/${storeId}/draft-orders/${id}`, method: 'DELETE'
    },
      options);
    }
  /**
 * Quantity, price override and customizations on a catalog line; title, SKU, price and shipping on a custom line. 422 for a field the line's kind does not have
 * @summary Edit one line in place
 */
export const patchAdminV1StoresStoreIdDraftOrdersIdLinesLineId = (
    { storeId, id, lineId }: PatchAdminV1StoresStoreIdDraftOrdersIdLinesLineIdPathParameters,
    patchAdminV1StoresStoreIdDraftOrdersIdLinesLineIdBody: BodyType<PatchAdminV1StoresStoreIdDraftOrdersIdLinesLineIdBody>,
 options?: SecondParameter<typeof customInstance<DraftOrder>>,) => {
      return customInstance<DraftOrder>(
      {url: `/admin/v1/stores/${storeId}/draft-orders/${id}/lines/${lineId}`, method: 'PATCH',
      headers: {'Content-Type': 'application/json', },
      data: patchAdminV1StoresStoreIdDraftOrdersIdLinesLineIdBody
    },
      options);
    }
  /**
 * Same customer, lines, addresses, shipping, discount and notes under a fresh number, in status draft. Works on completed and canceled drafts too
 * @summary Copy a draft into a new one
 */
export const postAdminV1StoresStoreIdDraftOrdersIdDuplicate = (
    { storeId, id }: PostAdminV1StoresStoreIdDraftOrdersIdDuplicatePathParameters,
 options?: SecondParameter<typeof customInstance<DraftOrder>>,) => {
      return customInstance<DraftOrder>(
      {url: `/admin/v1/stores/${storeId}/draft-orders/${id}/duplicate`, method: 'POST'
    },
      options);
    }
  /**
 * Subject, text and HTML with the store's template override applied; `message` previews the personal line
 * @summary The quote email as it would be sent now
 */
export const getAdminV1StoresStoreIdDraftOrdersIdInvoicePreview = (
    { storeId, id }: GetAdminV1StoresStoreIdDraftOrdersIdInvoicePreviewPathParameters,
    params?: GetAdminV1StoresStoreIdDraftOrdersIdInvoicePreviewParams,
 options?: SecondParameter<typeof customInstance<DraftInvoicePreview>>,) => {
      return customInstance<DraftInvoicePreview>(
      {url: `/admin/v1/stores/${storeId}/draft-orders/${id}/invoice/preview`, method: 'GET',
        params
    },
      options);
    }
  /**
 * Needs no blockers and an email (the draft's, or `to`). The email lists the lines, totals, the note, an optional personal `message` and how to pay with the store's manual methods. Sending again re-sends
 * @summary Email the quote to the customer
 */
export const postAdminV1StoresStoreIdDraftOrdersIdSendInvoice = (
    { storeId, id }: PostAdminV1StoresStoreIdDraftOrdersIdSendInvoicePathParameters,
    postAdminV1StoresStoreIdDraftOrdersIdSendInvoiceBody: BodyType<PostAdminV1StoresStoreIdDraftOrdersIdSendInvoiceBody>,
 options?: SecondParameter<typeof customInstance<DraftOrder>>,) => {
      return customInstance<DraftOrder>(
      {url: `/admin/v1/stores/${storeId}/draft-orders/${id}/send-invoice`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: postAdminV1StoresStoreIdDraftOrdersIdSendInvoiceBody
    },
      options);
    }
  /**
 * `manual` places it unpaid with the method's fee and instructions, like a storefront manual checkout; `paid` records the payment as already received. A walk-in draft with no email completes too; the order then has no email and sends none. Tracked stock is reserved and sold. 409 with `meta.blockers` while something is missing. Retry-safe by state: once completed, a repeat answers 409 with `meta.orderId` and `meta.orderName` instead of placing a second order. A `paid` completion's `note` becomes the payment_received timeline entry
 * @summary Turn the draft into an order
 */
export const postAdminV1StoresStoreIdDraftOrdersIdComplete = (
    { storeId, id }: PostAdminV1StoresStoreIdDraftOrdersIdCompletePathParameters,
    completeDraftOrderRequest: BodyType<CompleteDraftOrderRequest>,
 options?: SecondParameter<typeof customInstance<PostAdminV1StoresStoreIdDraftOrdersIdComplete201>>,) => {
      return customInstance<PostAdminV1StoresStoreIdDraftOrdersIdComplete201>(
      {url: `/admin/v1/stores/${storeId}/draft-orders/${id}/complete`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: completeDraftOrderRequest
    },
      options);
    }
  /**
 * @summary Cancel a draft or an invoiced quote
 */
export const postAdminV1StoresStoreIdDraftOrdersIdCancel = (
    { storeId, id }: PostAdminV1StoresStoreIdDraftOrdersIdCancelPathParameters,
 options?: SecondParameter<typeof customInstance<DraftOrder>>,) => {
      return customInstance<DraftOrder>(
      {url: `/admin/v1/stores/${storeId}/draft-orders/${id}/cancel`, method: 'POST'
    },
      options);
    }
  export type GetAdminV1StoresStoreIdDraftOrdersResult = NonNullable<Awaited<ReturnType<typeof getAdminV1StoresStoreIdDraftOrders>>>
export type PostAdminV1StoresStoreIdDraftOrdersResult = NonNullable<Awaited<ReturnType<typeof postAdminV1StoresStoreIdDraftOrders>>>
export type GetAdminV1StoresStoreIdDraftOrdersPaymentOptionsResult = NonNullable<Awaited<ReturnType<typeof getAdminV1StoresStoreIdDraftOrdersPaymentOptions>>>
export type GetAdminV1StoresStoreIdDraftOrdersIdResult = NonNullable<Awaited<ReturnType<typeof getAdminV1StoresStoreIdDraftOrdersId>>>
export type PatchAdminV1StoresStoreIdDraftOrdersIdResult = NonNullable<Awaited<ReturnType<typeof patchAdminV1StoresStoreIdDraftOrdersId>>>
export type DeleteAdminV1StoresStoreIdDraftOrdersIdResult = NonNullable<Awaited<ReturnType<typeof deleteAdminV1StoresStoreIdDraftOrdersId>>>
export type PatchAdminV1StoresStoreIdDraftOrdersIdLinesLineIdResult = NonNullable<Awaited<ReturnType<typeof patchAdminV1StoresStoreIdDraftOrdersIdLinesLineId>>>
export type PostAdminV1StoresStoreIdDraftOrdersIdDuplicateResult = NonNullable<Awaited<ReturnType<typeof postAdminV1StoresStoreIdDraftOrdersIdDuplicate>>>
export type GetAdminV1StoresStoreIdDraftOrdersIdInvoicePreviewResult = NonNullable<Awaited<ReturnType<typeof getAdminV1StoresStoreIdDraftOrdersIdInvoicePreview>>>
export type PostAdminV1StoresStoreIdDraftOrdersIdSendInvoiceResult = NonNullable<Awaited<ReturnType<typeof postAdminV1StoresStoreIdDraftOrdersIdSendInvoice>>>
export type PostAdminV1StoresStoreIdDraftOrdersIdCompleteResult = NonNullable<Awaited<ReturnType<typeof postAdminV1StoresStoreIdDraftOrdersIdComplete>>>
export type PostAdminV1StoresStoreIdDraftOrdersIdCancelResult = NonNullable<Awaited<ReturnType<typeof postAdminV1StoresStoreIdDraftOrdersIdCancel>>>
